export class ElevationHelpers {
    static _cubicInterpolate(v0, v1, v2, v3, t) {
        return v1 + 0.5 * t * (v2 - v0 + t * (2 * v0 - 5 * v1 + 4 * v2 - v3 + t * (3 * (v1 - v2) + v3 - v0)));
    }
    static getSafeElevation(elevations, w, h, xi, yi) {
        const clampedX = Math.min(Math.max(0, xi), w - 1);
        const clampedY = Math.min(Math.max(0, yi), h - 1);
        return elevations[clampedX + clampedY * w];
    }
    static GetElevationBilinear(elevations, w, h, xNorm, yNorm) {
        const x = xNorm * (w - 1);
        const y = yNorm * (h - 1);
        const x0 = Math.floor(x);
        const y0 = Math.floor(y);
        const x1 = Math.min(x0 + 1, w - 1);
        const y1 = Math.min(y0 + 1, h - 1);
        const q11 = elevations[x0 + y0 * w];
        const q21 = elevations[x1 + y0 * w];
        const q12 = elevations[x0 + y1 * w];
        const q22 = elevations[x1 + y1 * w];
        const dx = x - x0;
        const dy = y - y0;
        const r1 = q11 * (1 - dx) + q21 * dx;
        const r2 = q12 * (1 - dx) + q22 * dx;
        return r1 * (1 - dy) + r2 * dy;
    }
    static GetElevationCubic(elevations, w, h, xNorm, yNorm) {
        const x = xNorm * (w - 1);
        const y = yNorm * (h - 1);
        const x0 = Math.floor(x);
        const y0 = Math.floor(y);
        const dx = x - x0;
        const dy = y - y0;
        const values = [];
        for (let j = -1; j <= 2; j++) {
            const row = [];
            for (let i = -1; i <= 2; i++) {
                row.push(ElevationHelpers.getSafeElevation(elevations, w, h, x0 + i, y0 + j));
            }
            values.push(row);
        }
        const interpolatedRows = values.map((row) => ElevationHelpers._cubicInterpolate(row[0], row[1], row[2], row[3], dx));
        return ElevationHelpers._cubicInterpolate(interpolatedRows[0], interpolatedRows[1], interpolatedRows[2], interpolatedRows[3], dy);
    }
    static GetLastColumn(elevations, w, h) {
        const lastColumn = new elevations.constructor(h);
        for (let i = 0, w1 = w - 1; i < h; i++, w1 += w) {
            lastColumn[i] = elevations[w1];
        }
        return lastColumn;
    }
    static GetLastRow(elevations, w, h) {
        const startIndex = (h - 1) * w;
        return new elevations.constructor(elevations.buffer, elevations.byteOffset + startIndex * elevations.BYTES_PER_ELEMENT, w);
    }
    static GetFirstColumn(elevations, w, h, duplicateFirst = false) {
        const firstColumn = new elevations.constructor(h + (duplicateFirst ? 1 : 0));
        for (let i = 0; i < h; i++) {
            firstColumn[i] = elevations[i * w];
        }
        if (duplicateFirst) {
            firstColumn[h] = firstColumn[h - 1];
        }
        return firstColumn;
    }
    static GetFirstRow(elevations, w) {
        return new elevations.constructor(elevations.buffer, elevations.byteOffset, w);
    }
    static GetElevationAt(elevations, w, h, x, y) {
        if (x < 0 || x >= w || y < 0 || y >= h) {
            throw new Error("Coordinates out of bounds");
        }
        const index = x + y * w;
        const target = new elevations.constructor(1);
        target[0] = elevations[index];
        return target;
    }
    static GetColumn(elevations, w, h, colIndex) {
        const column = new elevations.constructor(h);
        for (let i = 0; i < h; i++) {
            column[i] = elevations[colIndex + i * w];
        }
        return column;
    }
    static GetRow(elevations, w, rowIndex) {
        const startIndex = rowIndex * w;
        const endIndex = startIndex + w;
        if (startIndex < 0 || endIndex > elevations.length) {
            throw new Error("Row index out of bounds");
        }
        return new elevations.constructor(elevations.buffer, elevations.byteOffset + startIndex * elevations.BYTES_PER_ELEMENT, w);
    }
    static GetArea(elevations, w, h, startX, startY, areaWidth, areaHeight) {
        if (startX < 0 || startY < 0 || startX + areaWidth > w || startY + areaHeight > h) {
            throw new Error("Specified area is out of bounds.");
        }
        const area = new elevations.constructor(areaWidth * areaHeight);
        for (let y = 0; y < areaHeight; y++) {
            const sourceStart = (startY + y) * w + startX;
            const destStart = y * areaWidth;
            area.set(elevations.subarray(sourceStart, sourceStart + areaWidth), destStart);
        }
        return area;
    }
    static CompareElevations(array1, array2, epsilon) {
        if (array1.length !== array2.length) {
            return false;
        }
        for (let i = 0; i < array1.length; i++) {
            if (Math.abs(array1[i] - array2[i]) > epsilon) {
                return false;
            }
        }
        return true;
    }
    static GetElevationsBetween(elevations, w, h, x1Norm, y1Norm, x2Norm, y2Norm, steps) {
        const result = new elevations.constructor(steps);
        const dxNorm = (x2Norm - x1Norm) / (steps - 1);
        const dyNorm = (y2Norm - y1Norm) / (steps - 1);
        for (let i = 0; i < steps; i++) {
            const xNorm = x1Norm + dxNorm * i;
            const yNorm = y1Norm + dyNorm * i;
            result[i] = ElevationHelpers.GetElevationBilinear(elevations, w, h, xNorm, yNorm);
        }
        return result;
    }
    static Normalize(elevations, minRange = 0, maxRange = 1) {
        const minValue = Math.min(...elevations);
        const maxValue = Math.max(...elevations);
        if (minValue === maxValue) {
            throw new Error("Normalization is not possible when all values are identical.");
        }
        const normalized = new elevations.constructor(elevations.length);
        const scale = (maxRange - minRange) / (maxValue - minValue);
        for (let i = 0; i < elevations.length; i++) {
            normalized[i] = minRange + (elevations[i] - minValue) * scale;
        }
        return normalized;
    }
}
//# sourceMappingURL=dem.helpers.js.map